import { CORRECTION_PREFERENCE_FILTER_IDS } from '../constants/filter-constants';
import {
  getFilterHandler,
  WRAPPED_ONLY_FILTER_IDS,
} from '../engine/filterRegistry';
import type { UnpackedFilter } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  type FilterId,
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
} from '../types/filters';
import { getSegmentSelectionNames } from './filterSegmentation';
import {
  formatFilterDisplay,
  getFilterOperatorText,
} from './formatFilterValue';
import { reviveFilterDates } from './revive-filter-dates';
import { unpackMultiFilter } from './unpackMultiFilter';

/** One chip's worth of a filter map, as the filter bar would draw it. */
export type FilterLine = {
  /** The filter's id: names collide (two filters are called "Geography"). */
  id: FilterId;
  /** Unique in the list: a multi filter gives one line per instance. */
  key: string;
  label: string;
  values: string[];
  kind: 'primary' | 'secondary';
  /** "is one of", "is not", "is from": what the filter's chip says. */
  operator?: string;
  /** The filter behind a secondary line, for the chip that previews it. */
  filter?: UnpackedFilter;
};

/**
 * The values a chip stands for, one string each, none dropped. Every member
 * of the Filter union has a case, so adding a kind fails typecheck here.
 */
const valuesOf = (filter: Filter): string[] => {
  switch (filter.type) {
    case FilterType.TREE:
    case FilterType.LIST:
    case FilterType.STATIC_LIST:
      return filter.selections.map((selection) => selection.name);
    case FilterType.FREE_TEXT:
      return filter.text;
    case FilterType.SCHOOL:
      return Object.values(filter.schoolResultItems).map(
        (school) => school.name
      );
    case FilterType.SEGMENTED: {
      // Tied to the range, as the chip reads: "0 to 10000 for Europe".
      const segments = getSegmentSelectionNames(filter.segmentations).join(
        ', '
      );
      const ranges = valuesOf(filter.wrappedFilter);
      return segments
        ? ranges.map((range) => `${range} for ${segments}`)
        : ranges;
    }
    case FilterType.RANGE:
      // JSON drops an open bound ("greater than" stores max: undefined), and
      // the formatter needs both keys present.
      return [
        formatFilterDisplay({ ...filter, min: filter.min, max: filter.max }),
      ];
    case FilterType.DATE:
    case FilterType.DATE_RANGE:
    case FilterType.TEXT:
    case FilterType.VALUE:
      // Dates read as the chip prints them ("2024-01 to 2024-06").
      return [formatFilterDisplay(filter)];
    case FilterType.MULTI:
    case FilterType.GROUP:
      // Described through their instances and children, never as themselves.
      return [];
    default: {
      const unhandled: never = filter;
      return unhandled;
    }
  }
};

/**
 * The chip rule: a filter is a chip when its handler says it is active, judged
 * on the range a segmented filter wraps, as useEffectiveFilters does. The
 * correction preferences (Data Adjustments, Contingent Workers) are the user's
 * account settings, not part of what a set means (the apply hook treats them
 * the same way), and their sync marks them user-submitted, so they are left
 * out by id.
 */
const isChip = (id: FilterId, filter: Filter): boolean => {
  if (CORRECTION_PREFERENCE_FILTER_IDS.includes(id)) return false;
  if (WRAPPED_ONLY_FILTER_IDS.has(id) && !isSegmentedFilter(filter)) {
    return false;
  }
  const handler = getFilterHandler(id);
  if (!handler) return false;
  const state = isSegmentedFilter(filter) ? filter.wrappedFilter : filter;
  return (handler.isActive as (state: Filter) => boolean)(state);
};

/**
 * Something the user set, as opposed to a preference or a dependency the
 * app synced into the map: the filter forms mark what they submit.
 */
const isUserSet = (filter: Filter): boolean => {
  if (filter.isUserSubmitted) return true;
  if (isMultiFilter(filter)) return filter.instances.some(isUserSet);
  if (isSegmentedFilter(filter)) return isUserSet(filter.wrappedFilter);
  return false;
};

/**
 * What a filter map says, primary entities first, then every filter the user
 * set and the filter bar would chip: one line per chip, so a multi filter
 * gives one per instance, each with its own operator. Defaults and synced
 * preferences are left out: a description shows what the user did.
 */
export const describeFilterEntries = (
  entries: Iterable<[FilterId, Filter]>
): FilterLine[] => {
  const primary: FilterLine[] = [];
  const secondary: FilterLine[] = [];
  for (const [id, filter] of entries) {
    if (filter.type === FilterType.TREE && filter.isPrimary) {
      const values = (filter.primarySelections ?? filter.selections)
        .map((selection) => selection.name)
        .filter(Boolean);
      if (values.length > 0) {
        primary.push({
          id,
          key: id,
          label: filter.name,
          values,
          kind: 'primary',
        });
      }
      continue;
    }
    if (filter.isPrimary || !isChip(id, filter) || !isUserSet(filter)) {
      continue;
    }
    for (const unpacked of unpackMultiFilter(id, filter)) {
      // A stored set is JSON, so its dates come back as text. Reviving
      // copies the filter, so the instance fields ride along.
      const chipFilter: UnpackedFilter = reviveFilterDates(unpacked);
      const values = valuesOf(chipFilter).filter(Boolean);
      if (values.length === 0) continue;
      secondary.push({
        id,
        key: chipFilter.displayId ?? id,
        label: filter.name,
        values,
        kind: 'secondary',
        // A segmented chip (SubFilterTag) words its range inside the value,
        // "from 0 to 10000", with no operator between name and value.
        operator: isSegmentedFilter(chipFilter)
          ? undefined
          : getFilterOperatorText(chipFilter).trim(),
        filter: chipFilter,
      });
    }
  }
  return [...primary, ...secondary];
};
